//* The member popup shared by the maps that pin members: the listing map's
//* view toggle and the location map on a place page. Assigns the popup's
//* markup builder onto the controller, which must already have useI18n's `t`
//* under the "view_components.map" scope.

//* Passed as the Popup's className, and what the popup's styles are scoped to
//* (see _member_listing.scss)
const MEMBER_POPUP_CLASS_NAME = "member-popup";

const escapeHtml = function(value) {
  return value == null ? "" : String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

//* A mini member card: thumbnail, name, village, match score (when ranked)
//* and a link to the member page. Properties come from our own markers
//* response but are escaped anyway.
const memberPopupHtml = function({ label_title, village, stays, cta, url, image_url, image_lowres_url, match_score, match_max, match_details }) {
  const escape = escapeHtml;

  //* Explicit colors: the popup background is always white regardless of the
  //* page theme, so themed text/link colors would wash out in dark mode
  //* A fixed 3:2 frame (matching the imgix crop) layering spinner -> blurred
  //* low-res -> full-res, so the popup opens at its final size instead of
  //* jumping when the photo arrives. lazysizes observes DOM mutations, so the
  //* lazyload class works on popup HTML injected after page load.
  const image = image_url ? `<div class="member-popup-photo mb-2">
      <img src="${escape(image_lowres_url)}" alt="" aria-hidden="true">
      <img data-src="${escape(image_url)}" alt="" class="lazyload member-popup-photo-full">
    </div>` : "";
  //* Inline SVG (FontAwesome location-dot): Fa embeds icons server-side, so a
  //* client-created <i> tag would render nothing here
  const villageIcon = "<svg viewBox=\"0 0 384 512\" fill=\"currentColor\" aria-hidden=\"true\" style=\"width: 0.85em; height: 0.85em;\"><path d=\"M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z\"/></svg>";
  const villageHtml = village ? `<div class="d-flex align-items-center gap-1" style="color: #6C757D;">${villageIcon}${escape(village)}</div>` : "";
  //* What a place carries in place of a village: how many accommodations it
  //* holds. Inline SVG (FontAwesome house) for the same reason as above.
  const staysIcon = "<svg viewBox=\"0 0 576 512\" fill=\"currentColor\" aria-hidden=\"true\" style=\"width: 0.85em; height: 0.85em;\"><path d=\"M303.5 5.7c-9-7.6-22.1-7.6-31.1 0l-264 224c-10.1 8.6-11.3 23.7-2.8 33.8s23.7 11.3 33.8 2.8L64 245.5 64 432c0 44.2 35.8 80 80 80l288 0c44.2 0 80-35.8 80-80l0-186.5 24.5 20.8c10.1 8.6 25.3 7.3 33.8-2.8s7.3-25.3-2.8-33.8l-264-224zM240 304l96 0c8.8 0 16 7.2 16 16l0 144-128 0 0-144c0-8.8 7.2-16 16-16z\"/></svg>";
  const staysHtml = stays ? `<div class="d-flex align-items-center gap-1" style="color: #6C757D;">${staysIcon}${escape(stays)}</div>` : "";
  //* Opens the listing's shared match-breakdown modal (#matchDetailsModal, filled
  //* by members--match-details — the popup sits inside that controller's scope).
  //* match_details arrives as a JSON string: Mapbox stringifies nested feature
  //* properties, which is exactly what the modal controller parses. Only the
  //* listing map ranks its members, so a place page's popups carry none.
  const infoIcon = "<svg viewBox='0 0 512 512' fill='currentColor' aria-hidden='true' style='width: 1em; height: 1em;'><path d='M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z'/></svg>";
  const matchInfoButton = !match_details ? "" : `<button
      type="button"
      class="member-popup-match-info"
      data-bs-toggle="modal"
      data-bs-target="#matchDetailsModal"
      data-action="members--match-details#open"
      data-match-name="${escape(label_title)}"
      data-match-details="${escape(match_details)}"
      aria-label="${this.t("members.match_info")}"
    >${infoIcon}</button>`;
  const matchHtml = match_max == null ? "" : `<div class="fw-medium d-flex align-items-center gap-1" style="color: #6C757D;">
      ${this.t("members.match", { score: escape(match_score), max: escape(match_max) })}
      ${matchInfoButton}
    </div>`;

  //* Only the button navigates — the rest of the card is inert, so a stray
  //* tap while panning the map doesn't leave the page
  return `<div style="min-width: 200px; color: #212529;">
      ${image}
      <div class="fw-bold">${escape(label_title)}</div>
      ${villageHtml}
      ${staysHtml}
      ${matchHtml}
      <a href="${escape(url)}" class="btn btn-primary w-100 mt-2">${cta ? escape(cta) : this.t("members.view_details")}</a>
    </div>`;
};

const useMemberPopup = controller => {
  Object.assign(controller, { memberPopupHtml });
};

export { MEMBER_POPUP_CLASS_NAME, memberPopupHtml, useMemberPopup };

export default useMemberPopup;
